// MobileNavDrawer — panneau de navigation mobile complet (slide-in depuis la
// gauche + backdrop). Corrige un vrai trou fonctionnel : avant ce composant,
// la barre du bas mobile n'exposait que 6 liens sur la trentaine que
// NAV_MODULES définit (Pixels, Tracking, Stratégie, Studio, Stores,
// Crédits... étaient invisibles sur mobile). Même source de données que
// DesktopSidebar (NAV_MODULES) pour rester cohérent entre les deux.
import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSignOutAlt, faRocket, faXmark, faUserCircle } from "@fortawesome/free-solid-svg-icons";
import logo from "assets/logo.png";
import { getNavModules, isLinkActive } from "./navModules";
import CreditBalancePill from "./CreditBalancePill";
import { useLang } from "../../../../i18n/LanguageContext";
import LanguageSwitcher from "../../../../i18n/LanguageSwitcher";

const MobileNavDrawer = ({ open, onClose, user, handleLogout, unreadCount = 0, persona = "default" }) => {
  const location = useLocation();
  const path = location.pathname;
  const { t } = useLang();

  // Empêche le scroll du fond pendant que le tiroir est ouvert.
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  return (
    <div
      className={`md:hidden fixed inset-0 z-50 transition-opacity duration-200 ${open ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0"}`}
      aria-hidden={!open}
    >
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
        onClick={onClose}
      />
      <div
        className={`absolute inset-y-0 left-0 w-[82%] max-w-[320px] bg-white shadow-2xl flex flex-col transition-transform duration-300 ease-out ${open ? "translate-x-0" : "-translate-x-full"}`}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-center justify-between px-4 pt-4 pb-3 border-b border-gray-100">
          <img src={logo} alt="OnebotAds" className="h-7" />
          <button
            onClick={onClose}
            aria-label="Fermer le menu"
            className="w-9 h-9 flex items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 active:scale-95 transition"
          >
            <FontAwesomeIcon icon={faXmark} className="text-lg" />
          </button>
        </div>

        <div className="px-4 pt-3">
          <CreditBalancePill />
        </div>

        <nav className="flex-1 overflow-y-auto px-2 pt-3 pb-4 space-y-4">
          {getNavModules(persona).map((mod) => (
            <div key={mod.key}>
              <div className="flex items-center gap-2 px-2 mb-1.5">
                <span className={`w-1.5 h-1.5 rounded-full ${mod.dot}`} />
                <span className={`text-[10px] uppercase font-bold tracking-wide ${mod.accent}`}>{t(mod.label)}</span>
              </div>
              <ul className="space-y-0.5">
                {mod.links.map((link) => {
                  const active = isLinkActive(link, path);
                  return (
                    <li key={link.to}>
                      <Link
                        to={link.to}
                        onClick={onClose}
                        className={`flex items-center gap-3 rounded-xl px-3 py-3 text-sm transition active:scale-[0.98] ${
                          active ? `${mod.activeBg} shadow-sm font-semibold` : `text-gray-600 ${mod.hover}`
                        }`}
                      >
                        <FontAwesomeIcon icon={link.icon} className="text-sm w-4" />
                        <span className="truncate">{t(link.label)}</span>
                        {link.badge === "unread" && unreadCount > 0 && (
                          <span className={`ml-auto rounded-full px-1.5 py-0.5 text-[10px] font-bold ${active ? "bg-white/25 text-white" : "bg-[#221C51] text-white"}`}>
                            {unreadCount > 99 ? "99+" : unreadCount}
                          </span>
                        )}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>

        <div className="p-3 border-t border-gray-100 bg-[#0A1A58]/5">
          <Link to="/dashboard/profile" onClick={onClose} className="flex items-center gap-2 py-1.5 active:opacity-70 transition">
            <FontAwesomeIcon icon={faUserCircle} className="text-2xl text-[#221C51]" />
            <div className="min-w-0">
              <p className="font-bold text-[#221C51] text-sm truncate">{user?.name}</p>
              <p className="text-[11px] text-[#221C51]/70 truncate">{user?.email}</p>
            </div>
          </Link>
          <Link
            to="/dashboard/billing"
            onClick={onClose}
            className="mt-2 w-full bg-white text-[#221C51] text-sm px-3 py-2.5 rounded-full active:bg-gray-100 flex items-center justify-center gap-2 transition border border-gray-200"
          >
            <FontAwesomeIcon icon={faRocket} className="text-sm" />
            {user?.isPremium ? t("Gérer l'abonnement") : "Passer Pro"}
          </Link>
          <div className="mt-2 flex justify-center">
            <LanguageSwitcher className="w-full text-center" />
          </div>
          <button
            onClick={() => { onClose(); handleLogout(); }}
            className="mt-1 w-full text-gray-400 text-xs active:text-gray-600 flex items-center justify-center gap-1.5 py-2"
          >
            <FontAwesomeIcon icon={faSignOutAlt} className="text-xs" />
            <span>{t('Déconnexion')}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default MobileNavDrawer;
